import React, { createElement } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import { availableOffersTitles } from '../../../../constants/factoring/summary-titles';
import { getSummaryActionButtonTooltipBlocked } from '../../../../constants/factoring/operation-unavailability-codes';
import DocumentSummary from '../../../../components/summaries/DocumentSummary';
import SummaryData from '../../../../components/summaries/SummaryData';
import ShoppingCartSummaryActionButton from '../../../../components/shopping/ShoppingCartSummary/ShoppingCartSummaryActionButton';
import OrderingDebtWarning from '../../../../components/ordering/OrderingDebtWarning';

const getTooltipTitle = ({ isBlocked, isActionDisabled, unavailabilityCode }) => {
  if (isBlocked) return getSummaryActionButtonTooltipBlocked(unavailabilityCode);
  if (isActionDisabled) return availableOffersTitles.summaryActionButtonTooltip;
  return '';
};

const AvailableOffersDateSelectionStep = ({
  clientsIds,
  offerIds,
  headers,
  isActionDisabled,
  setShowConfirmationSummary,
  setShowCessionStep,
  MY_CLIENTS,
  countryConfig,
  SummaryComponent,
  isBlocked,
  unavailabilityCode,
  showGoBackButton,
  getClientColumns,
  getDetailsColumns,
  detailsHeaders,
  detailsMobileHeaders,
  getInformationStackText,
}) => (
  <DocumentSummary
    summaryTitle={availableOffersTitles.summaryTitle}
    summaryDescription={availableOffersTitles.summaryDescription}
    documentTypeId="offers-date-selection"
    SummaryTable={createElement(SummaryComponent, {
      selectedClients: clientsIds,
      selectedDocuments: offerIds,
      queryDocument: MY_CLIENTS,
      defaultOrderBy: 'name',
      summaryHeaders: countryConfig.clientHeaders,
      mobileHeaders: headers,
      onSelectionModelChange: () => {},
      showInformationStack: countryConfig.showInformationStack,
      useSelectDaysButtons: countryConfig.useSelectDaysButtons,
      minDaysFromToday: countryConfig.minDaysFromToday,
      informationStackText: getInformationStackText({ isBlocked, unavailabilityCode }),
      getClientColumns,
      getDetailsColumns,
      detailsHeaders,
      detailsMobileHeaders,
    })}
    SummaryData={(
      <SummaryData
        title={availableOffersTitles.summaryDataTitle}
        cartType="available-offers"
        infoTitle={availableOffersTitles.summaryDataInfoTitle}
        info={countryConfig.summaryDataInfo}
        WarningAlert={OrderingDebtWarning}
        getWarningParams={(shoppingCartData) => ({
          orderingDebt: shoppingCartData?.orderingDebt,
          hasPurchaseOrdersNearToDebt: shoppingCartData?.hasPurchaseOrdersNearToDebt,
        })}
      />
    )}
    ActionButton={(
      <ShoppingCartSummaryActionButton
        tooltipTitle={getTooltipTitle({ isBlocked, isActionDisabled, unavailabilityCode })}
        disabled={isActionDisabled || isBlocked}
        id="go-to-cessions-button"
        onClick={() => {
          setShowCessionStep(true);
        }}
        label={(
          <Typography variant="small_3" color="white" fontWeight={700}>
            {countryConfig.summaryActionButtonText}
          </Typography>
        )}
      />
    )}
    showGoBackButton={showGoBackButton}
    showTableInMobile
    onGoBackClick={() => setShowConfirmationSummary(false)}
  />
);

AvailableOffersDateSelectionStep.propTypes = {
  clientsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  offerIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  isActionDisabled: PropTypes.bool.isRequired,
  setShowConfirmationSummary: PropTypes.func.isRequired,
  setShowCessionStep: PropTypes.func.isRequired,
  MY_CLIENTS: PropTypes.shape({}).isRequired,
  countryConfig: PropTypes.shape({
    clientHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
    summaryDataInfo: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      equation: PropTypes.string,
      alt: PropTypes.string,
      width: PropTypes.number,
    })).isRequired,
    summaryActionButtonText: PropTypes.string.isRequired,
    showInformationStack: PropTypes.bool.isRequired,
    useSelectDaysButtons: PropTypes.bool.isRequired,
    minDaysFromToday: PropTypes.number,
  }).isRequired,
  SummaryComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  isBlocked: PropTypes.bool,
  unavailabilityCode: PropTypes.string,
  showGoBackButton: PropTypes.bool,
  getClientColumns: PropTypes.func.isRequired,
  getDetailsColumns: PropTypes.func.isRequired,
  detailsHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  detailsMobileHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  getInformationStackText: PropTypes.func.isRequired,
};

AvailableOffersDateSelectionStep.defaultProps = {
  isBlocked: false,
  unavailabilityCode: null,
  showGoBackButton: false,
};

export default AvailableOffersDateSelectionStep;
